var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { Observable, ReplaySubject } from "rxjs";
/**
 * An asynchronous, prioritized, observable and removable queue of worker functions.
 *
 * Async - Workers are async functions. Next worker will be started when promise returned by previous worker resolves or rejects.
 *
 * Prioritized - Each worker has a priority. 0 is highest, higher numbers means lower priority.
 * Worker with higher priority will be executed first.
 *
 * Observable - Calling the add method returns an observable. The worker will be queued on subscribing this observable.
 * Multiple subscriptions lead to multiple executions of the worker.
 * This observable will emit the result of the finished worker and then will complete immediately.
 * It will send an error if the worker throws an error or rejects the returned promise.
 *
 * Removable - The worker will be removed from the queue if the subscriber unsubscribes early, before the worker is started.
 * If the subscriber unsubscribes late (the worker was already called but hasn't finished yet), it will not be cancelled.
 * The caller will just miss the result.
 *
 * Unhandled Errors - An error thrown by a worker will be passed through to the observable subscriber.
 * If the subscriber unsubscribes during the execution of the worker, and the worker throws an error the error will be passed the the
 * unhandledErrors observable (together with the worker function as a hint who throws that error).
 *
 */
var AsyncPrioritizedQueue = /** @class */ (function () {
    function AsyncPrioritizedQueue() {
        this.priorities = new Set();
        this.workers = new Map();
        this.isRunning = false;
        this.unhandledErrors$ = new ReplaySubject(1);
    }
    Object.defineProperty(AsyncPrioritizedQueue.prototype, "unhandledErrors", {
        get: function () {
            return this.unhandledErrors$;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Creates an Observable for queuing the given worker with the given priority.
     * Subscribe to add the worker to the queue. Multiple subscriptions will add the worker multiple times.
     * Unsubscribe before completion to remove the worker from the queue.
     *
     * Observable emits the result of the worker and then completes immediately.
     * Observable mirrors any error thrown by the worker.
     *
     * @param worker
     * @param priority
     */
    AsyncPrioritizedQueue.prototype.add = function (worker, priority) {
        var _this = this;
        if (priority < 0) {
            throw new Error("priority should not be smaller than 0");
        }
        return new Observable(function (subscriber) {
            _this.priorities.add(priority);
            if (!_this.workers.has(priority)) {
                _this.workers.set(priority, []);
            }
            var workers = _this.workers.get(priority);
            if (workers === undefined) {
                throw new Error("bug in implementation of this method, workers should not be undefined");
            }
            var unsubscribed = false;
            var workerWrapper = function () { return __awaiter(_this, void 0, void 0, function () {
                var result, e_1;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            _a.trys.push([0, 3, , 4]);
                            if (!!unsubscribed) return [3 /*break*/, 2];
                            return [4 /*yield*/, worker()];
                        case 1:
                            result = _a.sent();
                            subscriber.next(result);
                            _a.label = 2;
                        case 2:
                            subscriber.complete();
                            return [3 /*break*/, 4];
                        case 3:
                            e_1 = _a.sent();
                            if (unsubscribed) {
                                this.unhandledErrors$.next({
                                    error: e_1,
                                    worker: worker
                                });
                            }
                            else {
                                subscriber.error(e_1);
                            }
                            return [3 /*break*/, 4];
                        case 4:
                            this.start();
                            return [2 /*return*/];
                    }
                });
            }); };
            workers.push(workerWrapper);
            _this.start();
            return function () {
                unsubscribed = true;
            };
        });
    };
    AsyncPrioritizedQueue.prototype.start = function () {
        return __awaiter(this, void 0, void 0, function () {
            var worker;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!!this.isRunning) return [3 /*break*/, 2];
                        worker = this.getNextWorker();
                        if (worker === undefined) {
                            return [2 /*return*/];
                        }
                        this.isRunning = true;
                        return [4 /*yield*/, worker()];
                    case 1:
                        _a.sent();
                        this.isRunning = false;
                        this.start();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    AsyncPrioritizedQueue.prototype.getNextWorker = function () {
        var priorities = Array.from(this.priorities.values()).sort();
        if (priorities.length === 0) {
            return undefined;
        }
        var nextPriority = priorities[0];
        var workers = this.workers.get(nextPriority);
        if (workers === undefined || workers.length === 0) {
            return undefined;
        }
        var worker = workers.shift();
        if (workers.length === 0) {
            this.priorities.delete(nextPriority);
            this.workers.delete(nextPriority);
        }
        return worker;
    };
    return AsyncPrioritizedQueue;
}());
export { AsyncPrioritizedQueue };
